// AppDialog.js — Centered modal for CONFIRMATIONS and small forms (≤3 fields).
//
// The form-container rule: create/edit forms with 4+ fields use FormDrawer
// (right slide-over). AppDialog is only for confirmations, option pickers,
// and forms with 3 or fewer fields. Max width 480px, 10px radius.
// No decorative icons in the title.
//
// Usage:
//   <AppDialog
//     open={open}
//     onClose={handleClose}
//     title="Reject Claim"
//     actions={
//       <>
//         <AppButton variant="secondary" onClick={handleClose}>Cancel</AppButton>
//         <AppButton variant="danger" onClick={handleReject}>Reject</AppButton>
//       </>
//     }
//   >
//     <FormField label="Reason" required>...</FormField>
//   </AppDialog>
import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export default function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  showClose = true,
  maxWidth = 480,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth,
          m: 2,
        },
      }}
    >
      {/* Header — plain text title, no decorative icons */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: 'grey.900' }} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {showClose && (
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Content — 24px padding, fields stack at 16px */}
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
      </Box>

      {/* Footer — sticky bar (slate-50 / slate-100 border): Cancel first, action last */}
      {actions && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'grey.100',
            bgcolor: 'grey.50',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {actions}
        </Box>
      )}
    </Dialog>
  );
}
